import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Mail, Users, Send, TrendingUp, History, Settings, MailOpen, LogOut, Menu, X, ShieldOff, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { clearToken } from '@/lib/axiosSetup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/email/status`)
      .then((res) => setEmailStatus(res.data))
      .catch(() => setEmailStatus(null));
  }, []);

  const statusDotClass = !emailStatus
    ? 'bg-[#cbd5e1]'
    : emailStatus.dry_run
    ? 'bg-[#f59e0b]'
    : emailStatus.configured
    ? 'bg-[#10b981] animate-pulse'
    : 'bg-[#ef4444]';

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Kontrol Paneli' },
    { path: '/templates', icon: Mail, label: 'Şablonlar' },
    { path: '/recipients', icon: Users, label: 'Alıcılar' },
    { path: '/send', icon: Send, label: 'Kampanya Gönder' },
    { path: '/automations', icon: Workflow, label: 'Otomasyonlar' },
    { path: '/campaigns', icon: TrendingUp, label: 'Kampanya Raporları' },
    { path: '/replies', icon: MailOpen, label: 'Gelen Cevaplar' },
    { path: '/history', icon: History, label: 'Geçmiş' },
    { path: '/suppressions', icon: ShieldOff, label: 'Engel Listesi' },
    { path: '/settings', icon: Settings, label: 'Ayarlar' },
  ];

  const handleLogout = () => {
    clearToken();
    toast.success('Çıkış yapıldı');
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-[#ffffff]">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-[#e2e8f0]"
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-screen w-60 bg-[#f8fafc] border-r border-[#e2e8f0] flex flex-col z-40 transition-transform duration-300 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="shrink-0 px-5 py-4 border-b border-[#e2e8f0]">
          <h1 className="text-xl font-bold text-[#0f172a] tracking-tight">EmailPro</h1>
          <p className="text-xs text-[#64748b] mt-0.5">Pazarlama Paneli</p>
        </div>

        {/* min-h-0 + overflow-y-auto: menu yuksekligi ekrani astiginda
            (kucuk ekran / cok menu ogesi) sidebar kendi icinde kayabilsin. */}
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setIsSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-white text-[#6366f1] shadow-sm font-semibold'
                    : 'text-[#64748b] hover:text-[#0f172a] hover:bg-white/50'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={18} strokeWidth={isActive ? 2.5 : 1.5} />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="shrink-0 p-3 border-t border-[#e2e8f0] space-y-2">
          {/* Servis durumu backend'den okunur — sabit "Resend" yazisi degil. */}
          <div className="bg-white rounded-lg p-3 border border-[#e2e8f0]">
            <p className="text-xs font-medium text-[#64748b] mb-1.5">E-posta Servisi</p>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${statusDotClass}`}></div>
              <span className="text-sm font-medium text-[#0f172a]">
                {emailStatus ? emailStatus.service : '...'}
              </span>
            </div>
            {emailStatus?.dry_run && (
              <p className="mt-1.5 text-[11px] leading-tight font-semibold text-amber-700">
                TEST MODU — mail gönderilmiyor
              </p>
            )}
            {emailStatus && !emailStatus.configured && !emailStatus.dry_run && (
              <p className="mt-1.5 text-[11px] leading-tight font-semibold text-red-600">
                Yapılandırılmamış
              </p>
            )}
          </div>

          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2 text-[#64748b] hover:text-red-600 hover:bg-red-50 hover:border-red-200"
          >
            <LogOut size={16} />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 lg:ml-60 p-4 lg:p-6 pt-16 lg:pt-6">
        <div className="mx-auto w-full max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;